import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  Image,
  Input,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  UseDisclosureProps,
} from "@chakra-ui/react";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BaseUrl } from "../../../const.ts";
import { useBookingTickets } from "../../../hooks/useBookingTickets.ts";
import { useUserDetails } from "../../../hooks/useUserDetails.ts";
import { ITicketsResponse } from "../../../model.ts";
import { IframeClosed } from "./IframeClose.tsx";

interface IProps {
  orderConfirmed: ITicketsResponse;
  setOrderConfirmed: (x: null) => void;
  onClose: UseDisclosureProps["onClose"];
}

export const BookingTickets = ({
  orderConfirmed,
  setOrderConfirmed,
}: IProps) => {
  const { t } = useTranslation();
  const methods = useForm({
    mode: "all",
  });
  const [time, setTime] = useState(900);
  const [checkUpdate, setCheckUpdate] = useState(false);

  useEffect(() => {
    if (checkUpdate) {
      const firstTicket = methods.getValues("eventTicketOrderInfo")[0];
      const updatedTickets = methods
        .getValues("eventTicketOrderInfo")
        .map((ticket, index) => {
          return index === 0
            ? ticket
            : { ...firstTicket, idEventTicketInfo: ticket?.idEventTicketInfo };
        });
      methods.setValue("eventTicketOrderInfo", updatedTickets);
    }
  }, [checkUpdate, methods]);

  useEffect(() => {
    const arr = orderConfirmed.eventTicketInfos.map((item) => {
      return {
        name: "",
        surname: "",
        email: "",
        idEventTicketInfo: item.idEventTicketInfo,
      };
    });
    methods.reset({
      eventTicketOrderId: orderConfirmed.idEventTicketOrder,
      totalPrice: orderConfirmed.totalPrice,
      eventTicketOrderInfo: arr,
    });
  }, []);

  useEffect(() => {
    let timer = setInterval(() => {
      setTime((prevTime) => {
        if (prevTime === 0) {
          clearInterval(timer);
          return 0;
        } else {
          return prevTime - 1;
        }
      });
    }, 1000);

    return () => {
      clearInterval(timer); // Clear the timer when the component unmounts or when `time` changes.
    };
  }, []);

  const { loading, onSubmit, url, setUrl, closeSubmit } =
    useBookingTickets(setOrderConfirmed);
  const id = Cookies.get("userId");
  const { data: userData } = useUserDetails(id);

  if (url) {
    return (
      <IframeClosed
        url={url}
        setUrl={setUrl}
        id={orderConfirmed.idEventTicketOrder}
      />
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <Box
            position="absolute"
            top="15px"
            right="5px"
            bg="gray.800"
            color="white"
            px={4}
            py={2}
            borderRadius="md"
            boxShadow="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minWidth="100px"
          >
            <Text
              fontSize="lg"
              fontWeight="bold"
              color={time === 300 ? "yellow.400" : "white"}
              letterSpacing="wider"
            >
              {`${Math.floor(time / 60)}`.padStart(2, "0")}:
              {`${time % 60}`.padStart(2, "0")}
            </Text>
          </Box>

          <ModalHeader textAlign="center" fontSize="2xl" fontWeight="bold">
            {t("fill-guest")}
          </ModalHeader>
          <ModalBody bg="gray.50" maxH="600px" overflow="auto" p={8}>
            {/* User Info */}
            <Flex
              w="full"
              p={4}
              bg="white"
              mb="20px"
              gap={4}
              alignItems="center"
              borderRadius="md"
              boxShadow="lg"
            >
              <Image
                boxSize="60px"
                borderRadius="full"
                src={`${BaseUrl}/images/${userData?.body?.userView?.profilePhoto}`}
                alt="User"
              />
              <Box>
                <Text fontWeight="bold" fontSize="lg">
                  {userData?.body?.userView?.userName}
                </Text>
                <Text color="gray.500">{userData?.body?.userView?.email}</Text>
              </Box>
            </Flex>

            {/* Checkbox for Updates */}
            <Checkbox
              mb="20px"
              isChecked={checkUpdate}
              onChange={() => setCheckUpdate(!checkUpdate)}
              size="lg"
            >
              {t("first-ticket-info-all")}
            </Checkbox>

            {/* Guest Information Form */}

            {orderConfirmed.eventTicketInfos.map((item, index) => (
              <Box key={index} mb="40px">
                <Text fontWeight="bold" fontSize="lg" mb={2}>
                  {`#Ticket ${index + 1}`}: {item.ticketName}
                </Text>
                <Flex gap={2}>
                  {/* Name Field */}
                  <FormControl
                    isInvalid={
                      !!methods.formState.errors.eventTicketOrderInfo?.[index]
                        ?.name
                    }
                  >
                    <Input
                      bg="white"
                      placeholder={t("placeholder-guest-name")}
                      {...methods.register(
                        `eventTicketOrderInfo.${index}.name`,
                        {
                          required: "Name is required",
                        }
                      )}
                    />
                    <FormErrorMessage>
                      {
                        methods.formState.errors.eventTicketOrderInfo?.[index]
                          ?.name?.message
                      }
                    </FormErrorMessage>
                  </FormControl>

                  {/* Surname Field */}
                  <FormControl
                    isInvalid={
                      !!methods.formState.errors.eventTicketOrderInfo?.[index]
                        ?.surname
                    }
                  >
                    <Input
                      bg="white"
                      placeholder={t("placeholder-guest-surname")}
                      {...methods.register(
                        `eventTicketOrderInfo.${index}.surname`,
                        {
                          required: "Surname is required",
                        }
                      )}
                    />
                    <FormErrorMessage>
                      {
                        methods.formState.errors.eventTicketOrderInfo?.[index]
                          ?.surname?.message
                      }
                    </FormErrorMessage>
                  </FormControl>

                  {/* Email Field */}
                  <FormControl
                    isInvalid={
                      !!methods.formState.errors.eventTicketOrderInfo?.[index]
                        ?.email
                    }
                  >
                    <Input
                      bg="white"
                      placeholder={t("placeholder-guest-email")}
                      {...methods.register(
                        `eventTicketOrderInfo.${index}.email`,
                        {
                          required: "Email is required",
                          pattern: {
                            value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                            message: "Email is invalid",
                          },
                        }
                      )}
                    />
                    <FormErrorMessage>
                      {
                        methods.formState.errors.eventTicketOrderInfo?.[index]
                          ?.email?.message
                      }
                    </FormErrorMessage>
                  </FormControl>
                </Flex>
              </Box>
            ))}
            {/* Total Price */}
            <Box fontWeight={600} mt={4}>
              {t("price")}: &nbsp;
              <Text as="span">{orderConfirmed.totalPrice}&nbsp;&#8380;</Text>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="red"
              mr={2}
              onClick={() => {
                setOrderConfirmed(null);
                closeSubmit(orderConfirmed.idEventTicketOrder);
              }}
            >
              {t("Close")}
            </Button>
            <Button
              type="submit"
              onClick={methods.handleSubmit(onSubmit)}
              colorScheme="blue"
              isLoading={loading}
            >
              {t("prosesed-to-payment")}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </>
  );
};
